import React, { Component, useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError, ExpandingTextarea } from '../../components';

import css from './FieldTextInput.module.css';

const CONTENT_MAX_LENGTH = 5000;

const FieldTextInputComponent = props => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    rootClassName,
    className,
    inputRootClass,
    customErrorText,
    id,
    label,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    hideErrorMessage,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const isTextarea = input.type === 'textarea';
  const isPassword = input.type === 'password';

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  const effectiveType = isPassword && isPasswordVisible ? 'text' : type;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Also remove type from uncontrolled spread to avoid overwriting effectiveType
  const { type: _omitType, ...inputWithoutValueAndType } = inputWithoutValue;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};

  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      [css.inputSuccess]: valid,
      [css.inputError]: hasError,
      [css.textarea]: isTextarea,
    });
  const maxLength = CONTENT_MAX_LENGTH;
  const inputProps = isTextarea
    ? {
      className: inputClasses,
      id,
      rows: 1,
      maxLength,
      ...refMaybe,
      ...inputWithoutType,
      ...rest,
    }
    : isUncontrolled
      ? {
        className: inputClasses,
        id,
        defaultValue,
        ...refMaybe,
        ...inputWithoutValueAndType,
        ...rest,
        type: effectiveType,
      }
      : {
        className: inputClasses,
        id,
        ...refMaybe,
        ...inputWithoutType,
        ...rest,
        type: effectiveType,
      };

  const classes = classNames(rootClassName || css.root, className);
  return (
    <div className={classes}>
      {label ? <label htmlFor={id}>{label}</label> : null}
      {isTextarea ? (
        <ExpandingTextarea {...inputProps} />
      ) : !isPassword ? <input {...inputProps} /> : (
        <div className={isPassword ? css.passwordWrapper : undefined}>
          <input {...inputProps} />
          {isPassword ? (
            <button
              type="button"
              className={css.passwordToggle}
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
              onClick={() => setIsPasswordVisible(v => !v)}
            >
              {/* simple eye icon using SVG to avoid external deps */}
              {isPasswordVisible ? <svg
                className={css.passwordToggleIcon}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 5c-7 0-11 7-11 7s3.6 7 11 7 11-7 11-7-3.6-7-11-7zm0 12c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5zm0-8a3 3 0 100 6 3 3 0 000-6z" />
              </svg>
                : <svg fill="#000000" width="20px" height="20px" viewBox="0 -0.4 13.6 13.6" xmlns="http://www.w3.org/2000/svg"><title>hide</title><path d="m2.7 1.5 9 9 -0.8 0.8 -1.85 -1.85Q8.026 10 6.8 10q-1.6 0 -2.95 -0.974 -1.376 -0.974 -2.25 -2.624 0.374 -0.674 0.95 -1.35 0.55 -0.7 1.05 -1.05L1.9 2.3zm6.5 4.9q0 -1 -0.7 -1.7T6.8 4l-1.074 -1.074q0.626 -0.124 1.074 -0.124 1.624 0 3 1 1.35 1 2.2 2.6 -0.15 0.324 -0.474 0.8t-0.674 0.85zM6.8 8.8q0.674 0 1.25 -0.35l-0.724 -0.724q-0.25 0.076 -0.524 0.076 -0.574 0 -0.974 -0.4 -0.426 -0.426 -0.426 -1 0 -0.224 0.1 -0.5l-0.75 -0.75q-0.35 0.574 -0.35 1.25 0 1 0.7 1.7t1.7 0.7"/></svg>

              }  </button>
          ) : null}
        </div>
      )}
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

FieldTextInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  isUncontrolled: false,
  inputRef: null,
};

FieldTextInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,

  onUnmount: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    // Either 'textarea' or something that is passed to the input element
    type: string.isRequired,
  }).isRequired,
  meta: object.isRequired,
};

class FieldTextInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTextInputComponent} {...this.props} />;
  }
}

export default FieldTextInput;
